<template>
  <page :loading="loading">
    <page-header :title="$t('IntelligenceDashboard_Title')"></page-header>
    <page-content v-if="visible">
      <div class="main-ct">
        <dashboard-card
          :card-description="$t('IntelligenceDashboard_DataEngine_Description')"
          :card-title="$t('IntelligenceDashboard_DataEngine_Title')"
          card-icon="intelligence-data-engine"
          :package-tag-name="$t('IntelligenceDashboard_Core_Tag')"
          :is-core-package="true"
          :show-package-tag="!$hasFeature('metabase')"
        >
          <template v-if="isDataEngineEnabled">
            <el-tooltip
              effect="light"
              :disabled="isDataEngineAccessible"
              :content="$t('IntelligenceDashboard_DontHavePermission')"
            >
              <router-link
                :to="projectRoute({ name: 'MetabaseProjectApplication' })"
              >
                <el-button
                  type="primary"
                  size="small"
                  :disabled="!isDataEngineAccessible"
                >
                  {{ $t('IntelligenceDashboard_ManageYourData_Btn') }}
                  <i class="el-icon-arrow-right" />
                </el-button>
              </router-link>
            </el-tooltip>
          </template>
          <template v-else>
            <a
              href="https://docs.xtremepush.com/docs/intelligence"
              target="_blank"
            >
              <el-button type="primary" plain size="small">
                {{ $t('IntelligenceDashboard_ReadMore_Btn') }}
              </el-button>
            </a>
            <el-button
              type="primary"
              plain
              @click="sendIntelligenceProductRequest"
              size="small"
            >
              {{ $t('IntelligenceDashboard_SendRequest_Btn') }}
            </el-button>
          </template>
        </dashboard-card>
        <dashboard-card
          :card-description="
            $t('IntelligenceDashboard_Computations_Description')
          "
          :card-title="$t('IntelligenceDashboard_Computations_Title')"
          card-icon="intelligence-computations"
          :package-tag-name="$t('IntelligenceDashboard_Core_Tag')"
          :is-core-package="true"
          :show-package-tag="!$hasFeature('metabase')"
        >
          <div class="active-actions-ct" v-if="$hasFeature('metabase')">
            <el-tag class="active-computations-tag">
              {{
                $t('IntelligenceDashboard_ActiveComputations', {
                  count: activeComputations
                })
              }}
            </el-tag>
            <el-tooltip
              effect="light"
              :disabled="$can('MetabaseComputation:ReadAccess')"
              :content="$t('IntelligenceDashboard_DontHavePermission')"
            >
              <router-link
                :to="projectRoute({ name: 'MetabaseComputationList' })"
              >
                <el-button
                  type="primary"
                  size="small"
                  :disabled="!$can('MetabaseComputation:ReadAccess')"
                >
                  {{ $t('IntelligenceDashboard_ReadComputations_Btn') }}
                  <i class="el-icon-arrow-right" />
                </el-button>
              </router-link>
            </el-tooltip>
          </div>
          <template v-else>
            <a
              href="https://docs.xtremepush.com/docs/intelligence"
              target="_blank"
            >
              <el-button type="primary" plain size="small">
                {{ $t('IntelligenceDashboard_ReadMore_Btn') }}
              </el-button>
            </a>
            <el-button
              type="primary"
              plain
              @click="sendIntelligenceProductRequest"
              size="small"
            >
              {{ $t('IntelligenceDashboard_SendRequest_Btn') }}
            </el-button>
          </template>
        </dashboard-card>
        <dashboard-card
          :card-description="$t('IntelligenceDashboard_Dashboards_Description')"
          :card-title="$t('IntelligenceDashboard_Dashboards_Title')"
          card-icon="intelligence-dashboards"
          :package-tag-name="$t('IntelligenceDashboard_Core_Tag')"
          :is-core-package="true"
          :show-package-tag="!$hasFeature('metabase')"
        >
          <div class="active-actions-ct" v-if="$hasFeature('metabase')">
            <el-tag class="active-computations-tag">
              {{
                $t('IntelligenceDashboard_ActiveDashboards', {
                  count: activeDashboards
                })
              }}
            </el-tag>
            <router-link :to="projectRoute({ name: 'MetabaseDashboardList' })">
              <el-button type="primary" size="small">
                {{ $t('IntelligenceDashboard_Dashboards_Title') }}
                <i class="el-icon-arrow-right" />
              </el-button>
            </router-link>
          </div>
          <template v-else>
            <a
              href="https://docs.xtremepush.com/docs/intelligence"
              target="_blank"
            >
              <el-button type="primary" plain size="small">
                {{ $t('IntelligenceDashboard_ReadMore_Btn') }}
              </el-button>
            </a>
            <el-button
              type="primary"
              plain
              @click="sendIntelligenceProductRequest"
              size="small"
            >
              {{ $t('IntelligenceDashboard_SendRequest_Btn') }}
            </el-button>
          </template>
        </dashboard-card>
        <dashboard-card
          :card-description="$t('IntelligenceDashboard_InfinityAI_Description')"
          :card-title="$t('IntelligenceDashboard_InfinityAI_Title')"
          card-icon="intelligence-infinityai"
          :package-tag-name="$t('IntelligenceDashboard_Plus_Tag')"
          :is-plus-package="true"
          :show-package-tag="!$hasFeature('mlStudio')"
        >
          <div class="active-actions-ct" v-if="$hasFeature('mlStudio')">
            <el-tag class="active-computations-tag">
              {{
                $t('IntelligenceDashboard_ActiveModels', {
                  count: activeModels
                })
              }}
            </el-tag>
            <el-tooltip
              effect="light"
              :disabled="!isModelsBtnDisabled()"
              :content="$t('IntelligenceDashboard_DontHavePermission')"
            >
              <router-link
                :to="projectRoute({ name: 'MlStudio', hash: '#/model' })"
              >
                <el-button
                  :disabled="isModelsBtnDisabled()"
                  type="primary"
                  size="small"
                >
                  {{ $t('IntelligenceDashboard_Models_Btn') }}
                  <i class="el-icon-arrow-right" />
                </el-button>
              </router-link>
            </el-tooltip>
          </div>
          <template v-else>
            <a
              href="https://docs.xtremepush.com/docs/intelligence"
              target="_blank"
            >
              <el-button type="primary" plain size="small">{{
                $t('IntelligenceDashboard_ReadMore_Btn')
              }}</el-button>
            </a>
            <el-button
              type="primary"
              plain
              @click="sendInfinityAIProductRequest"
              size="small"
              >{{ $t('IntelligenceDashboard_SendRequest_Btn') }}</el-button
            >
          </template>
        </dashboard-card>
      </div>
    </page-content>
  </page>
</template>

<script>
import PageHeader from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'
import { customConfirm, projectEndpoint, projectRoute } from '@/core/helpers'
import { ProductRequestTypesEnum } from '@/common/enum/ProductRequestTypesEnum'
import RestApi from '@/core/services/RestApi'
import Page from '@/common/components/page/Page'
import DashboardCard from '@/modules/customer-intelligence/components/DashboardCard'

export default {
  name: 'CustomerIntelligenceDashboardPage',
  components: { DashboardCard, Page, PageContent, PageHeader },
  created() {
    this.loadData()
  },
  data() {
    return {
      activeComputations: 0,
      activeDashboards: 0,
      activeModels: 0,
      loading: true,
      visible: false
    }
  },
  methods: {
    isModelsBtnDisabled() {
      const firstCondition =
        this.$store.state.app.user.role === 'admin' ||
        this.$store.state.app.user.role === 'admin-support'

      const secondCondition =
        this.$store.state.app.user.role !== 'admin' &&
        this.$store.state.app.user.role !== 'admin-support' &&
        this.$can('MLStudio:ReadAccess') &&
        this.$store.state.app.user.infinityai

      return !firstCondition && !secondCondition
    },
    loadData() {
      let promises = []
      this.fetchEndpoints.forEach(endpoint => {
        if (endpoint) {
          promises.push(
            RestApi(endpoint)
              .then(({ data }) => {
                endpoint.handler(data)
              })
              .catch(error => {
                this.loading = false
                this.$message({
                  message: error.message,
                  type: 'error'
                })
              })
          )
        }
      })

      Promise.all(promises).then(() => {
        this.loading = false
        this.visible = true
      })
    },
    projectRoute,
    sendIntelligenceProductRequest() {
      customConfirm(
        this.$t('CustomerIntelligencePage_Interested_In_Customer_Intelligence'),
        this.$t(
          'CustomerIntelligencePage_Interested_In_Customer_Intelligence_Description'
        ),
        this.$t(
          'CustomerIntelligencePage_Interested_In_Customer_Intelligence_Send_Request'
        ),
        this.$t(
          'CustomerIntelligencePage_Interested_In_Customer_Intelligence_Cancel'
        )
      )
        .then(() => {
          RestApi({
            method: 'post',
            url: `projects/${this.$route.query.project_id}/notification`,
            data: {
              productName: ProductRequestTypesEnum.CUSTOMER_INTELLIGENCE
            }
          })
            .then(() => {
              this.$message({
                message: this.$t(
                  'CustomerIntelligencePage_Interested_In_Customer_Intelligence_Request_Sent'
                ),
                type: 'success'
              })
            })
            .catch(() => {
              this.$message({
                message: this.$t(
                  'CustomerIntelligencePage_Interested_In_Customer_Intelligence_Request_Failed'
                ),
                type: 'error'
              })
            })
        })
        .catch(() => {})
    },
    sendInfinityAIProductRequest() {
      customConfirm(
        this.$t('InfinityAI_Interested_In_Request_Title'),
        this.$t('InfinityAI_Interested_In_Request_Description'),
        this.$t('InfinityAI_Interested_In_Request_SendRequest'),
        this.$t('InfinityAI_Interested_In_Request_Cancel')
      )
        .then(() => {
          RestApi({
            method: 'post',
            url: `projects/${this.$route.query.project_id}/notification`,
            data: {
              productName: ProductRequestTypesEnum.INFINITY_AI
            }
          })
            .then(() => {
              this.$message({
                message: this.$t('InfinityAI_Interested_In_Request_Sent'),
                type: 'success'
              })
            })
            .catch(() => {
              this.$message({
                message: this.$t('InfinityAI_Interested_In_Request_Failed'),
                type: 'error'
              })
            })
        })
        .catch(() => {})
    }
  },
  computed: {
    isDataEngineEnabled() {
      return this.$hasFeature('metabase')
    },

    isDataEngineAccessible() {
      return !!(
        this.$can('MetabaseProject:ReadAccess') &&
        window.xp.metabaseProjectToken
      )
    },

    fetchEndpoints() {
      return [
        this.fetchMetabaseComputationsEndpoint,
        this.fetchMetabaseDashboardEndpoint,
        this.fetchActiveModelsEndpoint
      ]
    },

    fetchMetabaseComputationsEndpoint() {
      if (!this.$hasFeature('metabase')) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: 'metabase-computations',
        handler: data => {
          this.activeComputations = data.filter(
            computations => computations.status === 'verified'
          ).length
        }
      })
    },

    fetchMetabaseDashboardEndpoint() {
      if (!this.$hasFeature('metabase')) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: `metabase-dashboards`,
        handler: data => {
          this.activeDashboards = data.length
        }
      })
    },

    fetchActiveModelsEndpoint() {
      if (!this.$hasFeature('mlStudio')) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: `ml-studio/active-models-count`,
        handler: data => {
          this.activeModels = data
        }
      })
    }
  }
}
</script>

<style scoped>
>>> .page-content {
  background-color: #ffffff;
  height: calc(100vh - 64px);
}

.main-ct {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-flow: column;
}

.active-computations-tag {
  background-color: #ebeef5;
  color: #9298a8;
  width: fit-content;
}

.active-actions-ct {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}
</style>



// WEBPACK FOOTER //
// src/modules/customer-intelligence/pages/CustomerIntelligenceDashboardPage.vue